<template>
  <div class="site-management">
    <!-- 页面头部 -->
    <div class="header">
      <div class="header-left">
        <button class="back-btn" @click="goBack">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" stroke-width="2" stroke-linecap="round"
              stroke-linejoin="round" />
          </svg>
        </button>
        <div class="title">媒体管理</div>
      </div>
    </div>

    <!-- 操作工具栏 -->
    <div class="toolbar">
      <div class="search-filters">
        <div style="width: 100%;display: flex; align-items: center; gap: 10px;">
          <el-input style="flex: 1;" v-model="searchForm.siteId" placeholder="媒体ID" clearable />
          <el-input style="flex: 1;" v-model="searchForm.siteName" placeholder="媒体名称" clearable />
        </div>
        <el-select v-model="searchForm.isActive" placeholder="全部状态" clearable>
          <el-option label="全部状态" :value="null" />
          <el-option label="启用" :value="true" />
          <el-option label="禁用" :value="false" />
        </el-select>

        <div style="width: 100%;display: flex; align-items: center; gap: 10px;">
          <button @click="openAddModal" class="btn btn-primary">
            <i class="icon-plus"></i> 新增
          </button>
          <button @click="fetchSites" class="btn btn-secondary">
            <i class="icon-search"></i> 搜索
          </button>
          <button @click="resetSearch" class="btn btn-outline">
            <i class="icon-refresh"></i> 重置
          </button>
        </div>
      </div>
    </div>

    <!-- 内容列表 -->
    <div class="content-table-container">
      <el-table :data="sites" style="width: 100%" empty-text="暂无媒体ID数据" v-loading="loading"
        height="calc(100vh - 225px)">
        <el-table-column prop="id" label="ID" width="80" align="center"></el-table-column>
        <el-table-column prop="siteId" label="媒体ID" width="120" align="center"></el-table-column>
        <el-table-column prop="siteName" label="媒体名称" min-width="150" show-overflow-tooltip></el-table-column>
        <el-table-column prop="description" label="描述" min-width="200" show-overflow-tooltip>
          <template slot-scope="scope">
            <span>{{ scope.row.description || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="isActive" label="状态" width="80" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.isActive ? 'success' : 'danger'" size="small">
              {{ scope.row.isActive ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" label="创建时间" width="150" align="center">
          <template slot-scope="scope">
            <span>{{ formatDateTime(scope.row.createTime) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="updateTime" label="更新时间" width="150" align="center">
          <template slot-scope="scope">
            <span>{{ formatDateTime(scope.row.updateTime) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" min-width="160" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="primary" @click="openEditModal(scope.row)">
              编辑
            </el-button>
            <el-button size="mini" :type="scope.row.isActive ? 'warning' : 'success'"
              @click="toggleStatus(scope.row.id)">
              {{ scope.row.isActive ? '禁用' : '启用' }}
            </el-button>
            <el-button size="mini" type="danger" @click="deleteItem(scope.row.id)">
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination" v-if="totalPages > 1">
        <button @click="prevPage" :disabled="currentPage <= 0">上一页</button>
        <span>{{ currentPage + 1 }} / {{ totalPages }}</span>
        <button @click="nextPage" :disabled="currentPage >= totalPages - 1">下一页</button>
      </div>
    </div>

    <!-- 新增/编辑模态框 -->
    <div v-if="isModalOpen" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <h3>{{ isEditMode ? '编辑媒体ID' : '新增媒体ID' }}</h3>
        <div class="form-group">
          <label>媒体ID:</label>
          <el-input v-model="currentSite.siteId" :disabled="isEditMode" clearable />
        </div>
        <div class="form-group">
          <label>媒体名称:</label>
          <el-input v-model="currentSite.siteName" clearable />
        </div>
        <div class="form-group">
          <label>描述:</label>
          <textarea v-model="currentSite.description" rows="3"></textarea>
        </div>
        <div class="form-group" style="display: flex; align-items: center;gap: 10px;">
          <label>是否启用:</label>
          <el-switch v-model="currentSite.isActive" />
        </div>
        <div class="modal-actions">
          <el-button @click="saveSite" class="btn-primary">保存</el-button>
          <el-button @click="closeModal" class="btn-outline">取消</el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getAllSites,
  createSite,
  updateSite,
  deleteSite,
  toggleSiteStatus
} from '@/utils/jdApi'

export default {
  name: 'SiteManagement',
  data() {
    return {
      loading: false,
      sites: [],
      isModalOpen: false,
      isEditMode: false,
      currentSite: {
        siteId: '',
        siteName: '',
        description: '',
        isActive: true
      },
      currentPage: 0,
      pageSize: 10,
      totalPages: 0,
      searchForm: {
        siteId: '',
        siteName: '',
        isActive: null
      }
    }
  },
  mounted() {
    this.fetchSites()
  },
  methods: {
    async fetchSites() {
      this.loading = true
      try {
        const params = {
          page: this.currentPage,
          size: this.pageSize,
          siteId: this.searchForm.siteId || undefined,
          siteName: this.searchForm.siteName || undefined,
          isActive: this.searchForm.isActive
        }
        const response = await getAllSites(params)
        if (response.success) {
          this.sites = response.data
          this.totalPages = response.totalPages
        } else {
          this.$message?.error?.('加载媒体ID列表失败')
        }
      } catch (error) {
        console.error('加载媒体ID列表失败:', error)
        this.$message?.error?.('加载媒体ID列表失败')
      } finally {
        this.loading = false
      }
    },

    resetSearch() {
      this.searchForm = {
        siteId: '',
        siteName: '',
        isActive: null
      }
      this.currentPage = 0
      this.fetchSites()
    },

    openAddModal() {
      this.isEditMode = false
      this.currentSite = {
        siteId: '',
        siteName: '',
        description: '',
        isActive: true
      }
      this.isModalOpen = true
    },

    openEditModal(site) {
      this.isEditMode = true
      this.currentSite = {
        siteId: site.siteId,
        siteName: site.siteName,
        description: site.description || '',
        isActive: site.isActive
      }
      this.editingId = site.id
      this.isModalOpen = true
    },

    async saveSite() {
      try {
        let response
        if (this.isEditMode) {
          response = await updateSite(this.editingId, this.currentSite)
        } else {
          response = await createSite(this.currentSite)
        }

        if (response.success) {
          this.closeModal()
          this.fetchSites()
        } else {
          this.$message?.error?.('保存失败')
        }
      } catch (error) {
        this.$message?.error?.('保存失败')
      }
    },

    async toggleStatus(siteId) {
      if (!confirm('确定要切换状态吗？')) {
        return
      }

      try {
        const response = await toggleSiteStatus(siteId)
        if (response.success) {
          this.fetchSites()
        } else {
          this.$message?.error?.('操作失败')
        }
      } catch (error) {
        console.error('切换状态失败:', error)
        this.$message?.error?.('操作失败')
      }
    },

    async deleteItem(siteId) {
      if (!confirm('确定要删除这个媒体ID吗？此操作不可恢复！')) {
        return
      }

      try {
        const response = await deleteSite(siteId)
        if (response.success) {
          this.fetchSites()
        } else {
          this.$message?.error?.('删除失败')
        }
      } catch (error) {
        console.error('删除媒体ID失败:', error)
        this.$message?.error?.('删除失败')
      }
    },

    closeModal() {
      this.isModalOpen = false
      this.isEditMode = false
      this.editingId = null
    },

    prevPage() {
      if (this.currentPage > 0) {
        this.currentPage--
        this.fetchSites()
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages - 1) {
        this.currentPage++
        this.fetchSites()
      }
    },

    formatDateTime(dateTime) {
      if (!dateTime) return '-'
      return new Date(dateTime).toLocaleString('zh-CN')
    },

    goBack() {
      this.$router.go(-1)
    }
  }
}
</script>

<style scoped>
.site-management {
  margin: 0 auto;
  padding: 10px;
  background: #f8f9fa;
  height: 100vh;
  box-sizing: border-box;
}

.header {
  background: linear-gradient(135deg, #42b983, #369870);
  color: white;
  padding: 4px;
  margin-bottom: 10px;
  flex-shrink: 0;
  border-radius: 12px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.back-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  color: white;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.back-btn svg {
  width: 20px;
  height: 20px;
}

.title {
  font-size: 18px;
  font-weight: 600;
  color: white;
}

.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding: 10px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  flex-wrap: wrap;
  gap: 15px;
}

.search-filters {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.search-filters .el-input {
  width: 150px;
}

.search-filters .el-select {
  width: 120px;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  outline: none;
  flex: 1;
  justify-content: center;
}

.btn-primary {
  background: #42b983;
  color: white;
}

.btn-primary:hover {
  background: #369870;
}

.btn-secondary {
  background: #42b983;
  color: white;
}

.btn-secondary:hover {
  background: #42b983;
}

.btn-outline {
  background: transparent;
  color: #42b983;
  border: 1px solid #42b983;
}

.btn-outline:hover {
  background: #42b983;
  color: white;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.content-table-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow-x: auto;
  overflow-y: auto;
}

/* el-table 自定义样式 */
.content-table-container .el-table {
  border: none;
}

.content-table-container .el-table th {
  background: #f8f9fa;
  color: #495057;
  font-weight: 600;
  border-bottom: 2px solid #dee2e6;
}

.content-table-container .el-table td {
  border-bottom: 1px solid #f1f3f4;
}

.content-table-container .el-table tbody tr:hover {
  background-color: #f8f9fa;
}

/* 操作按钮样式 */
.content-table-container .el-button--mini {
  padding: 5px 8px;
  margin: 0 2px;
}

/* 响应式 & 滚动条优化，参考攻略管理 */
.content-table-container::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.content-table-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.content-table-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.content-table-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.content-table-container {
  scrollbar-width: thin;
  scrollbar-color: #c1c1c1 #f1f1f1;
}

@media (max-width: 768px) {
  .content-table-container {
    max-height: calc(100vh - 210px);
    overflow-x: auto;
    overflow-y: auto;
  }

  .content-table-container .el-table {
    min-width: 600px;
  }

  .content-table-container .el-button--mini {
    padding: 3px 6px;
    font-size: 12px;
  }
}

@media (max-width: 480px) {
  .content-table-container .el-table {
    min-width: 450px;
  }
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  padding: 20px;
  background: white;
}

.pagination button {
  padding: 8px 16px;
  border: 1px solid #ddd;
  background: white;
  cursor: pointer;
  border-radius: 4px;
}

.pagination button:hover:not(:disabled) {
  background: #f8f9fa;
}

.pagination button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  padding: 30px;
  border-radius: 8px;
  width: 500px;
  max-width: 90vw;
}

.modal-content h3 {
  margin-top: 0;
  margin-bottom: 20px;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-sizing: border-box;
}

.form-group textarea {
  resize: vertical;
  min-height: 80px;
}

.modal-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
}
</style>